import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { UserFullname } from '@r/components/user-fullname.tsx';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import pointsTranslation from '@r/translations/points-translation.ts';
import type { PointCollection } from '@admin/components/points/points-columns.tsx';
import { useApiAdminpointsGetCollection } from '@r/api/generated/client.ts';
import type { PointPointCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type CompanyPointsMovementsTableProps = {
  companyUniqueCode: string;
};

export function CompanyPointsMovementsTable({
  companyUniqueCode
}: CompanyPointsMovementsTableProps) {
  const { t } = useTranslation();
  const [apiPagination, setApiPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const columns = useMemo<ColumnDef<PointCollection>[]>(() => {
    return [
      {
        id: 'amount',
        header: t('label.company.point.amount'),
        cell: ({ row }) => pointsTranslation(t, row.original.amount, true)
      },
      {
        id: 'label',
        header: t('label.company.point.pattern'),
        cell: ({ row }) => row.original.label ?? ''
      },
      {
        id: 'valueDate',
        header: t('label.company.point.value.date'),
        cell: ({ row }) =>
          row.original.valueDate
            ? format(new Date(row.original.valueDate), 'dd/MM/yyyy', { locale: fr })
            : ''
      },
      {
        id: 'type',
        header: t('label.company.point.status'),
        cell: ({ row }) => row.original.status ?? ''
      },
      {
        id: 'importedBy',
        header: t('label.company.point.import.user.fullname'),
        cell: ({ row }) => (
          <UserFullname
            fullname={row.original.importedBy?.fullname ?? ''}
            isDeleted={row.original.importedBy?.isDeleted}
          />
        )
      },
      {
        id: 'importedAt',
        header: t('label.company.point.import.date'),
        cell: ({ row }) =>
          row.original.importedAt
            ? format(new Date(row.original.importedAt), 'dd/MM/yyyy', { locale: fr })
            : ''
      }
    ];
  }, [t]);

  const pointsQuery = useApiAdminpointsGetCollection(
    {
      'attributedToCompany.uniqueCode': companyUniqueCode,
      page: apiPagination.pageIndex + 1,
      perPage: apiPagination.pageSize,
      'order[valueDate]': 'desc',
      'order[id]': 'desc'
    },
    {
      query: {
        enabled: Boolean(companyUniqueCode)
      }
    }
  );
  useOrvalQueryErrorToast(pointsQuery, t('toast.error.label'));

  const data = useMemo<PointCollection[]>(() => {
    const points = (pointsQuery.data?.data as PointPointCollectionReadModel[]) ?? [];

    return points.map((point) => ({
      id: point.id,
      amount: point.amount,
      label: (point as unknown as { label?: string | null })?.label ?? null,
      status: point.typeLabel as PointCollection['status'],
      valueDate: point.valueDate ?? null,
      importedAt: (point as unknown as { createdAt?: string | null })?.createdAt ?? null,
      importedBy: {
        fullname: point.addedByUserWorkspace?.user?.fullname ?? null,
        isDeleted: point.addedByUserWorkspace?.user?.isDeleted
      },
      company: {
        name: point.attributedToCompany?.name,
        uniqueCode: point.attributedToCompany?.uniqueCode
      },
      user: {
        uniqueCode: (point.obtainedBy as unknown as { uniqueCode?: string | null })?.uniqueCode,
        fullname:
          (point.obtainedBy as unknown as { user?: { fullname?: string | null } })?.user
            ?.fullname ?? null
      }
    }));
  }, [pointsQuery.data]);

  const mappedTable = useDataTable<PointCollection, unknown>({
    data,
    columns,
    pageCount: pointsQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: false,
    manualPaginate: true,
    manualSort: false,
    manualFilter: false
  });

  useEffect(() => {
    const { pageIndex, pageSize } = mappedTable.table.getState().pagination;
    if (pageIndex !== apiPagination.pageIndex || pageSize !== apiPagination.pageSize) {
      setApiPagination({ pageIndex, pageSize });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mappedTable.table.getState().pagination.pageIndex,
    mappedTable.table.getState().pagination.pageSize
  ]);

  const isLoading = pointsQuery.isLoading || pointsQuery.isFetching;

  if (isLoading) {
    return (
      <DataTableSkeleton
        columnCount={6}
        cellWidths={['14rem', '12rem', '12rem', '10rem', '10rem']}
      />
    );
  }

  return (
    <DataTable table={mappedTable.table} totalItems={pointsQuery.data?.pagination?.totalItems} />
  );
}
